import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/providers/RouterShim';
import Alert from '../../styled-components/components/Alert';
import Container from '../../styled-components/components/Container';
import { PageSection } from '../../styled-components/components/PageSection';
import PageHeader from '../../styled-components/structures/PageHeader';

interface CustomError {
  data?: string;
  message?: string;
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  location: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  location: string;
}

class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, State> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, location: props.location };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: RouteErrorBoundaryProps, state: State) {
    // Reset error boundary when location changes
    if (props.location !== state.location) {
      return {
        hasError: false,
        error: undefined,
        location: props.location,
      };
    }
    return { location: props.location };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // biome-ignore lint/suspicious/noConsole: Log error for debugging purposes
    console.error('Route error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const RouteErrorBoundaryWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return <RouteErrorBoundary location={location.pathname}>{children}</RouteErrorBoundary>;
};

const RouteErrorFallback = ({ error }: { error?: Error }) => {
  const { t } = useTranslation(['other']);

  const customError = error as CustomError;
  const errorMessage =
    customError?.data ||
    customError?.message ||
    t('pageError.alertMessageUnknown', { ns: 'other' });

  return (
    <Container>
      <PageHeader
        heading={t('pageError.heading', { ns: 'other' })}
        preamble={<p>{t('pageError.description', { ns: 'other' })}</p>}
        breadcrumbs={[
          {
            displayName: t('activity', { ns: 'activity' }),
            link: {
              to: '/',
              title: t('activity', { ns: 'activity' }),
            },
          },
          {
            displayName: t('pageError.heading', { ns: 'other' }),
          },
        ]}
      />
      <PageSection>
        <Alert
          tone="error"
          heading={t('pageError.alertHeading', { ns: 'other' }) || undefined}
          message={errorMessage || t('pageError.alertMessageUnknown', { ns: 'other' })}
        />
      </PageSection>
    </Container>
  );
};

export default RouteErrorBoundaryWrapper;
